import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import GameCard from '@/components/feature/GameCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, games, siteConfig } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'rating' | 'newest'>('popular');
  const [selectedPlatform, setSelectedPlatform] = useState<string>('all');

  const category = categories.find((c) => c.slug === slug);

  const filteredGames = useMemo(() => {
    let result = slug ? games.filter((g) => g.category === slug) : games;
    if (selectedPlatform !== 'all') {
      result = result.filter((g) => g.platform.includes(selectedPlatform));
    }
    if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.downloads - a.downloads);
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'newest') {
      result = [...result].sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
    }
    return result;
  }, [slug, sortBy, selectedPlatform]);

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen bg-background-50 pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-400 hover:text-primary-300">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const platforms = ['all', 'PC', '安卓'];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen bg-background-50 pt-16">
        {/* Category Header */}
        <section className="relative py-12 md:py-16 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20abstract%20gaming%20texture%20background%2C%20geometric%20patterns%20in%20deep%20crimson%20and%20charcoal%2C%20subtle%20glow%20effects%2C%20atmospheric%20dark%20aesthetic%2C%20no%20text%2C%20clean%20professional%20backdrop&width=1800&height=400&seq=category-header-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/60 to-background-50"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '游戏分类', href: '/category/japanese' }, { label: category.name }]} />
            <div className="flex items-center gap-4 mt-2">
              <span className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary-500/15 text-primary-400">
                <i className={`${category.icon} text-2xl`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 font-bold">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Filters & Games */}
        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-foreground-500 whitespace-nowrap">平台：</span>
                {platforms.map((p) => (
                  <button
                    key={p}
                    onClick={() => setSelectedPlatform(p)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      selectedPlatform === p
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-300/30'
                    }`}
                  >
                    {p === 'all' ? '全部' : p}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
                {[
                  { value: 'popular' as const, label: '最热' },
                  { value: 'rating' as const, label: '评分' },
                  { value: 'newest' as const, label: '最新' },
                ].map((s) => (
                  <button
                    key={s.value}
                    onClick={() => setSortBy(s.value)}
                    className={`px-3 py-1.5 text-xs rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                      sortBy === s.value
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-300/30'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredGames.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {filteredGames.map((game) => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-400">
                  <i className="ri-inbox-line text-4xl"></i>
                </span>
                <p className="text-foreground-500">该分类暂无游戏</p>
              </div>
            )}
          </div>
        </section>

        {/* Related Categories */}
        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-heading text-foreground-900 font-bold mb-6">探索更多分类</h2>
            <div className="flex items-center gap-3 flex-wrap">
              {categories
                .filter((c) => c.slug !== slug)
                .slice(0, 6)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-2 bg-background-50 border border-background-300/30 rounded-full text-sm text-foreground-600 hover:text-primary-400 hover:border-primary-500/30 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: `${category.name} - ${siteConfig.name}`,
            description: category.description,
            url: `${siteConfig.url}/category/${category.slug}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
                { '@type': 'ListItem', position: 2, name: '游戏分类', item: `${siteConfig.url}/category/japanese` },
                { '@type': 'ListItem', position: 3, name: category.name },
              ],
            },
          }),
        }}
      />
    </>
  );
}